import type { FC, ReactNode } from 'react'
import type { SunDay, SunPosition } from '@/lib/sun'
import type { Office } from '@/types'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { useClock } from '@/hooks/use-clock'
import { formatZoneTime, sunDay, sunPosition } from '@/lib/sun'
import { PointCloud } from '@/components/generals/point-cloud'
import { SunPath } from '@/components/footer/sun-path'

interface FooterOfficeProps {
    office: Office
    building: string
    className?: string
}

interface SunReading {
    position: SunPosition
    day: SunDay
}

const SUN_REFRESH_MS = 30000

const formatCoordinate = (value: number, positive: string, negative: string): string => `${Math.abs(value).toFixed(4)} ${value >= 0 ? positive : negative}`

/**
 * One office column: the building turning slowly in points, the address and phone, the
 * coordinates, the live local time and a live sun readout computed for that city.
 */
export const FooterOffice: FC<FooterOfficeProps> = ({ office, building, className }): ReactNode => {
    const clock = useClock(office.timeZone)
    const [hovered, setHovered] = useState(false)
    const [sun, setSun] = useState<SunReading | null>(null)
    const shape = office.code === 'LIS' ? 'office-lis' : 'office-sto'
    const coordinates = `${formatCoordinate(office.lat, 'N', 'S')}, ${formatCoordinate(office.lon, 'E', 'W')}`

    useEffect(() => {
        const read = (): void => {
            const now = Date.now()

            setSun({
                position: sunPosition(now, office.lat, office.lon),
                day: sunDay(now, office.lat, office.lon, office.timeZone)
            })
        }

        read()

        const timer = window.setInterval(read, SUN_REFRESH_MS)
        const onVisible = (): void => {
            if (!document.hidden) read()
        }

        document.addEventListener('visibilitychange', onVisible)

        return () => {
            window.clearInterval(timer)
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [office.lat, office.lon, office.timeZone])

    const below = sun !== null && sun.position.altitude < 0
    const riseSet = !sun
        ? 'Rise --:-- / Set --:--'
        : sun.day.polar === 'day'
            ? 'Sun up all day'
            : sun.day.polar === 'night'
                ? 'Sun down all day'
                : `Rise ${formatZoneTime(sun.day.rise ?? 0, sun.day.offsetMinutes)} / Set ${formatZoneTime(sun.day.set ?? 0, sun.day.offsetMinutes)}`

    return (
        <section
            aria-labelledby={`footer-office-${office.code}`}
            onPointerEnter={() => setHovered(true)}
            onPointerLeave={() => setHovered(false)}
            className={cn('flex min-w-0 flex-col', className)}
        >
            <PointCloud
                shape={shape}
                label={`Point cloud of the ${office.city} office: ${building}`}
                budget='small'
                rotate='always'
                rotateSpeed={5}
                hoverBoost={3.2}
                hover={hovered}
                effects={false}
                grid
                className='aspect-[4/3] w-full border-b border-hairline'
            />

            <div className='flex flex-1 flex-col justify-between gap-6 p-4 desk:p-5'>
                <div>
                    <h2 id={`footer-office-${office.code}`} className='hud text-ink'>
                        {office.code} / {office.city}
                    </h2>

                    <address className='hud mt-3 not-italic text-ink'>
                        {office.address.map(line => (
                            <span key={line} className='block'>
                                {line}
                            </span>
                        ))}
                    </address>

                    <a href={office.phoneHref} className='hud mt-2 inline-block text-mute transition-colors duration-300 hover:text-signal'>
                        {office.phone}
                    </a>

                    <p className='hud mt-3 text-mute'>
                        {coordinates}
                    </p>
                </div>

                <div>
                    <p className='font-mono text-[20px] leading-none tracking-[0.04em] tabular-nums text-ink desk:text-[22px]' suppressHydrationWarning>
                        <span className='sr-only'>
                            Local time in {office.city}:&nbsp;
                        </span>

                        {clock.text}
                    </p>

                    <p className='hud mt-2 text-mute' suppressHydrationWarning>
                        {clock.offset || 'UTC'}
                    </p>

                    <div className='mt-4 border-t border-hairline pt-3'>
                        <p className={cn('hud', below ? 'text-mute' : 'text-ink')} suppressHydrationWarning>
                            {!sun
                                ? 'Sun alt --.-° / Az ---.-°'
                                : below
                                    ? 'Sun below horizon'
                                    : `Sun alt ${sun.position.altitude.toFixed(1)}° / Az ${sun.position.azimuth.toFixed(1)}°`}
                        </p>

                        <p className='hud mt-1 text-mute' suppressHydrationWarning>
                            {riseSet}
                        </p>

                        <div className='mt-3'>
                            <SunPath day={sun?.day ?? null} altitude={sun?.position.altitude ?? null} />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
